import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { MdCheckCircle, MdError, MdWarning, MdInfo, MdClose, MdDeleteForever, MdHelp } from 'react-icons/md';

const ModalContext = createContext(null);

export const useModal = () => {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('useModal must be inside ModalProvider');
  return ctx;
};

// ── Confirm Modal ─────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9000;
  padding: 16px;
  backdrop-filter: blur(2px);
`;

const ModalCard = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.xl};
  padding: 32px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
  animation: modalIn 0.2s ease-out;

  @keyframes modalIn {
    from { opacity: 0; transform: scale(0.94) translateY(-8px); }
    to   { opacity: 1; transform: scale(1) translateY(0); }
  }
`;

const ModalIconBox = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  background: ${p => p.danger
    ? `rgba(252, 129, 129, 0.15)`
    : `rgba(79, 209, 197, 0.12)`};

  svg {
    font-size: 1.75rem;
    color: ${p => p.danger ? p.theme.colors.danger : p.theme.colors.primary};
  }
`;

const ModalTitle = styled.h3`
  font-size: 1.0625rem;
  font-weight: 700;
  color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 8px;
  letter-spacing: -0.01em;
`;

const ModalMessage = styled.p`
  font-size: 0.875rem;
  color: ${p => p.theme.colors.textSecondary};
  margin: 0 0 28px;
  line-height: 1.6;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const ModalBtn = styled.button`
  padding: 9px 22px;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;

  ${p => p.variant === 'cancel' && `
    background: ${p.theme.colors.backgroundDark};
    color: ${p.theme.colors.textSecondary};
    &:hover { background: ${p.theme.colors.border}; color: ${p.theme.colors.textPrimary}; }
  `}

  ${p => p.variant === 'confirm' && !p.danger && `
    background: linear-gradient(135deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark});
    color: white;
    box-shadow: 0 4px 12px ${p.theme.colors.primary}40;
    &:hover { transform: translateY(-1px); box-shadow: 0 6px 18px ${p.theme.colors.primary}50; }
  `}

  ${p => p.variant === 'confirm' && p.danger && `
    background: linear-gradient(135deg, ${p.theme.colors.danger}, #E53E3E);
    color: white;
    box-shadow: 0 4px 12px ${p.theme.colors.danger}40;
    &:hover { transform: translateY(-1px); box-shadow: 0 6px 18px ${p.theme.colors.danger}50; }
  `}
`;

const ConfirmModalComponent = ({ title, message, confirmText, cancelText, danger, onConfirm, onCancel }) => (
  <Overlay onClick={onCancel}>
    <ModalCard onClick={e => e.stopPropagation()}>
      <ModalIconBox danger={danger}>
        {danger ? <MdDeleteForever /> : <MdHelp />}
      </ModalIconBox>
      <ModalTitle>{title || (danger ? 'Confirmar eliminación' : 'Confirmar acción')}</ModalTitle>
      <ModalMessage>{message}</ModalMessage>
      <ModalButtons>
        <ModalBtn variant="cancel" onClick={onCancel}>
          {cancelText || 'Cancelar'}
        </ModalBtn>
        <ModalBtn variant="confirm" danger={danger} onClick={onConfirm}>
          {confirmText || 'Confirmar'}
        </ModalBtn>
      </ModalButtons>
    </ModalCard>
  </Overlay>
);

// ── Toast ─────────────────────────────────────────────────────────────────────

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(24px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const ToastContainer = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 10px;
  pointer-events: none;
`;

const TOAST_COLORS = {
  success: { bg: 'rgba(72, 187, 120, 0.12)', border: '#48BB78', icon: '#48BB78' },
  error:   { bg: 'rgba(252, 129, 129, 0.12)', border: '#FC8181', icon: '#FC8181' },
  warning: { bg: 'rgba(246, 173, 85, 0.12)', border: '#F6AD55', icon: '#F6AD55' },
  info:    { bg: 'rgba(99, 179, 237, 0.12)', border: '#63B3ED', icon: '#63B3ED' },
};

const ToastItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: 12px;
  padding: 14px 16px;
  min-width: 300px;
  max-width: 400px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border-left: 4px solid ${p => TOAST_COLORS[p.type]?.border || '#63B3ED'};
  animation: ${slideIn} 0.25s ease-out;
  pointer-events: all;
`;

const ToastIconBox = styled.div`
  flex-shrink: 0;
  margin-top: 1px;
  svg {
    font-size: 1.25rem;
    color: ${p => TOAST_COLORS[p.type]?.icon || '#63B3ED'};
  }
`;

const ToastBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const ToastMessage = styled.div`
  font-size: 0.875rem;
  color: ${p => p.theme.colors.textPrimary};
  line-height: 1.5;
  font-weight: 500;
`;

const ToastClose = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.textLight};
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  border-radius: 4px;
  flex-shrink: 0;
  transition: color 0.15s;
  &:hover { color: ${p => p.theme.colors.textPrimary}; }
  svg { font-size: 1rem; }
`;

const TOAST_ICONS = {
  success: <MdCheckCircle />,
  error:   <MdError />,
  warning: <MdWarning />,
  info:    <MdInfo />,
};

const ToastItemComponent = ({ id, message, type, onClose }) => (
  <ToastItem type={type}>
    <ToastIconBox type={type}>{TOAST_ICONS[type] || TOAST_ICONS.info}</ToastIconBox>
    <ToastBody>
      <ToastMessage>{message}</ToastMessage>
    </ToastBody>
    <ToastClose onClick={() => onClose(id)}><MdClose /></ToastClose>
  </ToastItem>
);

// ── Provider ──────────────────────────────────────────────────────────────────

export const ModalProvider = ({ children }) => {
  const [confirmState, setConfirmState] = useState(null);
  const [toasts, setToasts] = useState([]);
  const resolveRef = useRef(null);

  const confirm = useCallback((message, options = {}) => {
    return new Promise(resolve => {
      resolveRef.current = resolve;
      setConfirmState({ message, ...options });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setConfirmState(null);
    resolveRef.current?.(true);
  }, []);

  const handleCancel = useCallback(() => {
    setConfirmState(null);
    resolveRef.current?.(false);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const toast = useCallback((message, type = 'info') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => dismissToast(id), 4500);
  }, [dismissToast]);

  return (
    <ModalContext.Provider value={{ confirm, toast }}>
      {children}

      {confirmState && (
        <ConfirmModalComponent
          {...confirmState}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}

      <ToastContainer>
        {toasts.map(t => (
          <ToastItemComponent key={t.id} {...t} onClose={dismissToast} />
        ))}
      </ToastContainer>
    </ModalContext.Provider>
  );
};
